/**
 * Redux slice for managing application error state
 * 
 * This slice handles the global error state used for displaying toast notifications and
 * tracking application errors. It provides actions for loading and clearing errors.
 * 
 * @see {@link src/store/error/error.selectors.ts} - Selectors for accessing error state
 * @see {@link src/providers/ErrorProvider.tsx} - Provider that consumes error state
 * @see {@link src/services/error.service.ts} - Service that dispatches error actions
 */
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

// Define the state type with proper error typing
interface ErrorState {
  error: string[] | null;
}

// Initialize with properly typed state
const initialState: ErrorState = {
  error: null,
};

const errorSlice = createSlice({
  name: 'error',
  initialState,
  reducers: {
    loadError: (state, action: PayloadAction<string>) => {
      // Initialize as array if null
      if (!Array.isArray(state.error)) {
        state.error = [];
      }
      // Now we know it's an array, we can push
      state.error.push(action.payload);
    },
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const errorActions = errorSlice.actions;

export default errorSlice;
